import React, { useState } from 'react';
import { Car, Edit2, Save, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const vehicleTypes = ['boda', 'tuktuk', 'car', 'van', 'truck'];

const VehicleInfoEditor = ({ profile, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    vehicleType: profile?.vehicleType || 'boda',
    vehicleName: profile?.vehicleName || '',
    vehicleColor: profile?.vehicleColor || '',
    vehicleYear: profile?.vehicleYear || '',
    licensePlate: profile?.licensePlate || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error: dbError } = await supabase?.from('driver_profiles')?.update({
        vehicle_type: formData?.vehicleType,
        vehicle_name: formData?.vehicleName,
        vehicle_color: formData?.vehicleColor,
        vehicle_year: formData?.vehicleYear ? parseInt(formData?.vehicleYear) : null,
        license_plate: formData?.licensePlate,
      })?.eq('user_id', user?.id);
      if (dbError) throw dbError;
      await onUpdate?.(formData);
      setEditing(false);
    } catch (err) {
      setError(err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'vehicleName', label: 'Vehicle Make & Model', placeholder: 'e.g. Honda CB125' },
    { key: 'vehicleColor', label: 'Color', placeholder: 'e.g. Red' },
    { key: 'vehicleYear', label: 'Year', placeholder: 'e.g. 2022', type: 'number' },
    { key: 'licensePlate', label: 'License Plate', placeholder: 'e.g. KCA 123A' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Car className="w-4 h-4 text-[#1a365d]" />
          <h3 className="font-semibold text-[#1a365d]">Vehicle Information</h3>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors">
            <Edit2 className="w-3 h-3" /> Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a365d] text-white rounded-lg text-xs font-medium hover:bg-[#2d5a87] transition-colors disabled:opacity-50">
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setError(''); }} className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="p-5 space-y-4">
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

        {/* Vehicle Type */}
        <div>
          <p className="text-xs text-slate-500 mb-2">Vehicle Type</p>
          {editing ? (
            <div className="flex flex-wrap gap-2">
              {vehicleTypes?.map(type => (
                <button
                  key={type}
                  onClick={() => setFormData(p => ({ ...p, vehicleType: type }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${formData?.vehicleType === type ? 'bg-[#1a365d] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {type}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm font-semibold text-slate-800 capitalize">{formData?.vehicleType}</p>
          )}
        </div>

        {/* Other fields */}
        <div className="grid grid-cols-2 gap-4">
          {fields?.map(({ key, label, placeholder, type }) => (
            <div key={key}>
              <p className="text-xs text-slate-500 mb-1">{label}</p>
              {editing ? (
                <input
                  type={type || 'text'}
                  value={formData?.[key]}
                  onChange={e => setFormData(p => ({ ...p, [key]: e?.target?.value }))}
                  placeholder={placeholder}
                  className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                />
              ) : (
                <p className="text-sm font-semibold text-slate-800">{formData?.[key] || '—'}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VehicleInfoEditor;
